import { useState } from 'react';
import { Heart, ChevronRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Section } from '../hooks/useSections';

interface HIHomeProps {
  sections: Section[];
  goToHISection: (id: string) => void;
}

// HI section descriptions (from mockup)
const hiDescriptions: Record<string, string> = {
  'hi-1': 'How OAK works for health insurance advisors',
  'hi-2': 'Understanding each stage of your pipeline',
  'hi-3': 'AI configuration specific to health insurance',
  'hi-4': 'All tags used in your health insurance pipeline',
  'hi-5': 'Customizable workflows for your business',
  'hi-6': 'Step-by-step guides for everyday tasks',
};

function getIcon(name: string) {
  return (Icons as any)[name] || Icons.FileText;
}

export default function HIHome({ sections, goToHISection }: HIHomeProps) {
  return (
    <div className="max-w-[900px] mx-auto">
      {/* Header */}
      <div className="text-center mb-10 pt-5">
        <div className="w-[70px] h-[70px] bg-gradient-to-br from-oak-green to-oak-blue rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-[0_8px_24px_rgba(16,185,129,0.25)]">
          <Heart size={36} className="text-white" />
        </div>
        <h1 className="text-[32px] font-extrabold text-navy mb-2">Health Insurance</h1>
        <p className="text-base text-content-secondary">
          Everything specific to your health insurance business
        </p>
      </div>

      {/* 6 Section Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {sections.map((section) => (
          <SectionCard
            key={section.id}
            section={section}
            description={hiDescriptions[section.id] || section.description || ''}
            onClick={() => goToHISection(section.id)}
          />
        ))}
      </div>
    </div>
  );
}

function SectionCard({
  section,
  description,
  onClick,
}: {
  section: Section;
  description: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const Icon = getIcon(section.icon);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex flex-col items-start p-6 bg-white border rounded-xl cursor-pointer transition-all duration-200 text-left ${
        hovered
          ? 'border-oak-green -translate-y-[3px] shadow-[0_12px_24px_rgba(16,185,129,0.15)]'
          : 'border-content-border shadow-[0_1px_3px_rgba(0,0,0,0.05)]'
      }`}
    >
      <div className="w-[50px] h-[50px] rounded-xl bg-oak-green/[0.15] flex items-center justify-center mb-4">
        <Icon size={26} className="text-oak-green" />
      </div>
      <div className="font-bold text-content-primary text-[17px] mb-1.5">{section.title}</div>
      <div className="text-sm text-content-secondary leading-relaxed mb-3">{description}</div>
      <div className="flex items-center gap-1 text-oak-green text-sm font-medium mt-auto">
        {section.total_topic_count} topics <ChevronRight size={16} />
      </div>
    </button>
  );
}
